import type { CSSProperties } from 'react';
import styles from './Avatar.module.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Deterministic 0–9 index from a name for the avatar colour pool. */
function avatarColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return hash % 10;
}

interface IAvatarProps {
  name: string;
  size?: number; // Diameter in pixels
  glyph?: string; // Shown instead of the initials, e.g. '#' for a channel
  tinted?: boolean; // Soft wash of a colour derived deterministically from the name
  tone?: 'neutral' | 'solid'; // Fill style: `neutral` = light chip, `solid` = filled (e.g. the user menu)
  className?: string;
}

export function Avatar({ name, glyph, size = 28, tinted = false, tone = 'neutral', className }: IAvatarProps) {
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.max(10, Math.round(size * 0.4)),
  };

  if (tinted) {
    (style as Record<string, string>)['--avatar-color'] = `var(--avatar-fg-${avatarColorIndex(name)})`;
  }

  const cls = [styles.avatar, styles[tone], tinted ? styles.tinted : '', className].filter(Boolean).join(' ');
  
  return (
    <span className={cls} style={style} aria-hidden="true">
      {glyph ?? initials(name)}
    </span>
  );
}
